<script setup>
/* global axios */
// Modal de criar/editar atendimento — paciente (busca/criar), profissional→serviço em cascata,
// data + horário com slots livres, convênio, valor, observações.
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import AModal from './AModal.vue';

const props = defineProps({
  accountId: { type: [String, Number], required: true },
  practitioners: { type: Array, default: () => [] },
  services: { type: Array, default: () => [] },
  healthPlans: { type: Array, default: () => [] },
  editing: { type: Object, default: null },
  prefill: { type: Object, default: () => ({}) },
});
const emit = defineEmits(['saved', 'close']);
const { t } = useI18n();
const base = `/api/v1/accounts/${props.accountId}/kanban`;

const form = ref({
  patient_id: null,
  patient_name: '',
  practitioner_id: '',
  service_id: '',
  date: '',
  time: '',
  health_plan_id: '',
  price: '',
  location: '',
  notes: '',
  status: 'scheduled',
});
const slots = ref([]);
const patientQuery = ref('');
const patientResults = ref([]);
const showNewPatient = ref(false);
const newPatient = ref({ name: '', phone: '' });

const servicesForPractitioner = computed(() => {
  const pr = props.practitioners.find(
    p => p.id === Number(form.value.practitioner_id)
  );
  return pr
    ? props.services.filter(s => (pr.service_ids || []).includes(s.id))
    : props.services;
});

const searchPatients = async () => {
  if (patientQuery.value.length < 2) {
    patientResults.value = [];
    return;
  }
  patientResults.value =
    (await axios.get(`${base}/patients`, { params: { q: patientQuery.value } }))
      .data.data || [];
};
const pickPatient = p => {
  form.value.patient_id = p.id;
  form.value.patient_name = `${p.name} (${p.phone})`;
  patientResults.value = [];
  patientQuery.value = '';
};
const createPatientInline = async () => {
  if (!newPatient.value.name.trim() || !newPatient.value.phone.trim()) return;
  const { data } = await axios.post(`${base}/patients`, newPatient.value);
  pickPatient(data.data);
  showNewPatient.value = false;
  newPatient.value = { name: '', phone: '' };
};
const loadSlots = async () => {
  if (
    !form.value.practitioner_id ||
    !form.value.service_id ||
    !form.value.date
  ) {
    slots.value = [];
    return;
  }
  slots.value =
    (
      await axios.get(`${base}/appointments/availability`, {
        params: {
          practitioner_id: form.value.practitioner_id,
          service_id: form.value.service_id,
          date: form.value.date,
        },
      })
    ).data.data || [];
};
const onServiceChange = () => {
  const s = props.services.find(x => x.id === Number(form.value.service_id));
  if (s?.default_price && !form.value.price) form.value.price = s.default_price;
  loadSlots();
};
const onPractitionerChange = () => {
  form.value.service_id = '';
  slots.value = [];
};

const save = async () => {
  if (
    !form.value.patient_id ||
    !form.value.practitioner_id ||
    !form.value.service_id ||
    !form.value.date ||
    !form.value.time
  )
    return;
  const appointment_at = new Date(
    `${form.value.date}T${form.value.time}:00`
  ).toISOString();
  const payload = {
    patient_id: form.value.patient_id,
    practitioner_id: form.value.practitioner_id,
    service_id: form.value.service_id,
    appointment_at,
    health_plan_id: form.value.health_plan_id || null,
    price: form.value.price || null,
    location: form.value.location,
    notes: form.value.notes,
    status: form.value.status,
  };
  if (props.editing)
    await axios.patch(`${base}/appointments/${props.editing.id}`, payload);
  else await axios.post(`${base}/appointments`, payload);
  emit('saved');
};

onMounted(() => {
  if (props.editing) {
    const a = props.editing;
    const d = new Date(a.appointment_at);
    form.value = {
      patient_id: a.patient_id,
      patient_name: a.patient_name,
      practitioner_id: a.practitioner_id,
      service_id: a.service_id,
      date: d.toISOString().slice(0, 10),
      time: d.toTimeString().slice(0, 5),
      health_plan_id: a.health_plan_id || '',
      price: a.price || '',
      location: a.location || '',
      notes: a.notes || '',
      status: a.status,
    };
    loadSlots();
  } else {
    form.value.date =
      props.prefill.date || new Date().toISOString().slice(0, 10);
    form.value.time = props.prefill.time || '';
  }
});
</script>

<template>
  <AModal
    :title="
      editing
        ? t('ATENDIMENTOS.appointment.edit')
        : t('ATENDIMENTOS.appointment.new')
    "
    @close="emit('close')"
  >
    <div>
      <label class="block mb-1 text-sm font-medium text-n-slate-12"
        >{{ t('ATENDIMENTOS.form.client') }} *</label
      >
      <div
        v-if="form.patient_id"
        class="flex items-center gap-2 p-2 border rounded-lg bg-n-alpha-1 border-n-weak"
      >
        <span class="flex-1 text-sm">{{ form.patient_name }}</span>
        <button class="text-xs text-n-ruby-11" @click="form.patient_id = null">
          {{ t('ATENDIMENTOS.appointment.removeClient') }}
        </button>
      </div>
      <template v-else>
        <input
          v-model="patientQuery"
          class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/40"
          :placeholder="t('ATENDIMENTOS.form.clientSearch')"
          @input="searchPatients"
        />
        <div
          v-if="patientResults.length"
          class="mt-1 overflow-y-auto border rounded-lg max-h-40 border-n-weak"
        >
          <button
            v-for="p in patientResults"
            :key="p.id"
            class="block w-full px-3 py-2 text-sm text-left hover:bg-n-alpha-1"
            @click="pickPatient(p)"
          >
            {{ p.name }} <span class="text-n-slate-10">{{ p.phone }}</span>
          </button>
        </div>
        <button
          class="mt-1 text-xs text-n-blue-11 hover:underline"
          @click="showNewPatient = !showNewPatient"
        >
          {{ t('ATENDIMENTOS.appointment.createNewClient') }}
        </button>
        <div
          v-if="showNewPatient"
          class="p-3 mt-1 space-y-2 border rounded-lg bg-n-alpha-1 border-n-weak"
        >
          <input
            v-model="newPatient.name"
            class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/40"
            :placeholder="t('ATENDIMENTOS.appointment.clientName')"
          />
          <input
            v-model="newPatient.phone"
            class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/40"
            :placeholder="t('ATENDIMENTOS.appointment.clientPhone')"
          />
          <button
            class="justify-center w-full inline-flex items-center gap-1.5 px-3.5 py-2 text-sm font-medium rounded-lg bg-n-brand text-white hover:brightness-95 disabled:opacity-60"
            @click="createPatientInline"
          >
            {{ t('ATENDIMENTOS.appointment.createAndSelect') }}
          </button>
        </div>
      </template>
    </div>

    <div>
      <label class="block mb-1 text-sm font-medium text-n-slate-12"
        >{{ t('ATENDIMENTOS.form.practitioner') }} *</label
      ><select
        v-model="form.practitioner_id"
        class="w-full pl-3 pr-9 py-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/40"
        @change="onPractitionerChange"
      >
        <option value="">
          {{ t('ATENDIMENTOS.form.selectPractitioner') }}
        </option>
        <option v-for="p in practitioners" :key="p.id" :value="p.id">
          {{ p.name }} — {{ p.specialty }}
        </option>
      </select>
    </div>
    <div>
      <label class="block mb-1 text-sm font-medium text-n-slate-12"
        >{{ t('ATENDIMENTOS.form.service') }} *</label
      ><select
        v-model="form.service_id"
        class="w-full pl-3 pr-9 py-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/40"
        :disabled="!form.practitioner_id"
        @change="onServiceChange"
      >
        <option value="">{{ t('ATENDIMENTOS.form.selectService') }}</option>
        <option v-for="s in servicesForPractitioner" :key="s.id" :value="s.id">
          {{ s.name }} ({{
            t('ATENDIMENTOS.form.durationMinutes', { n: s.duration_minutes })
          }})
        </option>
      </select>
    </div>

    <div class="flex gap-3">
      <div class="flex-1">
        <label class="block mb-1 text-sm font-medium text-n-slate-12"
          >{{ t('ATENDIMENTOS.form.date') }} *</label
        ><input
          v-model="form.date"
          type="date"
          class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/40"
          @change="loadSlots"
        />
      </div>
      <div class="flex-1">
        <label class="block mb-1 text-sm font-medium text-n-slate-12"
          >{{ t('ATENDIMENTOS.form.time') }} *</label
        >
        <select
          v-if="slots.length"
          v-model="form.time"
          class="w-full pl-3 pr-9 py-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/40"
        >
          <option value="">{{ t('ATENDIMENTOS.form.selectService') }}</option>
          <option v-for="s in slots" :key="s.iso" :value="s.time">
            {{ s.time }}
          </option>
        </select>
        <input
          v-else
          v-model="form.time"
          type="time"
          class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/40"
        />
      </div>
    </div>
    <div class="flex gap-3">
      <div class="flex-1">
        <label class="block mb-1 text-sm font-medium text-n-slate-12">{{
          t('ATENDIMENTOS.form.partner')
        }}</label
        ><select
          v-model="form.health_plan_id"
          class="w-full pl-3 pr-9 py-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/40"
        >
          <option value="">{{ t('ATENDIMENTOS.form.private') }}</option>
          <option v-for="h in healthPlans" :key="h.id" :value="h.id">
            {{ h.name }}
          </option>
        </select>
      </div>
      <div class="flex-1">
        <label class="block mb-1 text-sm font-medium text-n-slate-12">{{
          t('ATENDIMENTOS.form.value')
        }}</label
        ><input
          v-model="form.price"
          type="number"
          step="0.01"
          class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/40"
          :placeholder="t('ATENDIMENTOS.form.pricePlaceholder')"
        />
      </div>
    </div>
    <div>
      <label class="block mb-1 text-sm font-medium text-n-slate-12">{{
        t('ATENDIMENTOS.form.room')
      }}</label
      ><input
        v-model="form.location"
        class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/40"
        :placeholder="t('ATENDIMENTOS.form.roomPlaceholder')"
      />
    </div>
    <div>
      <label class="block mb-1 text-sm font-medium text-n-slate-12">{{
        t('ATENDIMENTOS.form.notes')
      }}</label
      ><textarea
        v-model="form.notes"
        rows="2"
        class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/40"
        :placeholder="t('ATENDIMENTOS.form.notesPlaceholder')"
      />
    </div>

    <template #footer>
      <button
        class="inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg border border-n-weak text-n-slate-11 bg-n-background hover:bg-n-alpha-1"
        @click="emit('close')"
      >
        {{ t('ATENDIMENTOS.appointment.cancel') }}
      </button>
      <button
        class="inline-flex items-center gap-1.5 px-3.5 py-2 text-sm font-medium rounded-lg bg-n-brand text-white hover:brightness-95 disabled:opacity-60"
        @click="save"
      >
        {{
          editing
            ? t('ATENDIMENTOS.appointment.save')
            : t('ATENDIMENTOS.appointment.schedule')
        }}
      </button>
    </template>
  </AModal>
</template>
